import { useThreadViewportStore } from '@assistant-ui/react';
import Collapse from '@mui/material/Collapse';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Stack, styled, Text } from 'shell/components';

const FIRST_STATUS_DELAY_MS = 3_000;
const SUB_STATUS_DELAY_INCREMENT_MS = 2_500;
const EMPTY_STATUSES = [];

const ChatLoadingActivity = ({
    getActivity,
    requestSalt = null,
    requestStartedAt = null,
    requestType = null,
    statuses = EMPTY_STATUSES,
    title = '',
}) => {
    const { t } = useTranslation();
    const threadViewportStore = useThreadViewportStore();
    const [fallbackSalt] = useState(() => Math.floor(Math.random() * 4294967296));
    const requestActivity = useMemo(() => {
        const salt = requestSalt ?? fallbackSalt;
        return getActivity ? getActivity(salt, { requestType }) : { statuses, title };
    }, [fallbackSalt, getActivity, requestSalt, requestType, statuses, title]);
    const requestStatuses = useMemo(() => requestActivity.statuses || [], [requestActivity.statuses]);
    const requestTitle = requestActivity.title || title;
    const requestStatusesRef = useRef(requestStatuses);
    requestStatusesRef.current = requestStatuses;
    const detailsId = useId();
    const [history, setHistory] = useState([]);
    const [isExpanded, setIsExpanded] = useState(true);

    useEffect(() => {
        if (!requestStatusesRef.current.length) {
            setHistory([]);
            return undefined;
        }

        const elapsed = requestStartedAt ? Math.max(0, Date.now() - requestStartedAt) : 0;
        const firstStatusAt = FIRST_STATUS_DELAY_MS;
        const revealedCount =
            elapsed < firstStatusAt
                ? 0
                : Math.min(
                      requestStatusesRef.current.length,
                      1 + Math.floor((elapsed - firstStatusAt) / SUB_STATUS_DELAY_INCREMENT_MS)
                  );

        setHistory(requestStatusesRef.current.slice(0, revealedCount));
        setIsExpanded(true);

        let timeoutId;
        let nextIndex = revealedCount;
        const scheduleNextStatus = () => {
            if (nextIndex >= requestStatusesRef.current.length) return;

            const nextStatusAt =
                nextIndex === 0 ? firstStatusAt : firstStatusAt + nextIndex * SUB_STATUS_DELAY_INCREMENT_MS;
            const delay = requestStartedAt ? Math.max(0, nextStatusAt - elapsed) : nextStatusAt;

            timeoutId = setTimeout(() => {
                const nextStatus = requestStatusesRef.current[nextIndex];
                nextIndex += 1;
                setHistory((currentHistory) => [...currentHistory, nextStatus]);
                scheduleNextStatus();
            }, delay);
        };

        scheduleNextStatus();

        return () => clearTimeout(timeoutId);
    }, [requestStartedAt, requestStatuses]);

    const scrollToBottom = useCallback(() => {
        requestAnimationFrame(() => {
            threadViewportStore.getState().scrollToBottom({ behavior: 'smooth' });
        });
    }, [threadViewportStore]);

    useEffect(() => {
        if (!history.length || !isExpanded) return;
        scrollToBottom();
    }, [history.length, isExpanded, scrollToBottom]);

    if (!requestStatuses.length) return null;

    return (
        <LoadingActivityRow spacing={1}>
            <LoadingActivitySummary direction={'row'} spacing={1}>
                {history.length > 0 ? (
                    <LoadingActivityHandle
                        aria-controls={detailsId}
                        aria-expanded={isExpanded}
                        aria-label={
                            isExpanded
                                ? t('chat:hugo.loading.collapse-label', 'Collapse loading details')
                                : t('chat:hugo.loading.expand-label', 'Expand loading details')
                        }
                        type={'button'}
                        onClick={() => {
                            setIsExpanded((currentValue) => !currentValue);
                        }}
                    >
                        <LoadingActivityToggleEntrance>
                            <LoadingActivityToggle isExpanded={isExpanded} kind={'chevronDown'} />
                        </LoadingActivityToggleEntrance>
                        <LoadingActivityTitle $isEntering={true} component={'span'} variant={'p3'} weight={'medium'}>
                            {requestTitle}
                        </LoadingActivityTitle>
                    </LoadingActivityHandle>
                ) : null}
                {!history.length ? (
                    <LoadingActivityTitle variant={'p3'} weight={'medium'}>
                        {requestTitle}
                    </LoadingActivityTitle>
                ) : null}
            </LoadingActivitySummary>

            <Collapse
                in={isExpanded && history.length > 0}
                mountOnEnter={true}
                timeout={{ enter: 220, exit: 160 }}
                unmountOnExit={true}
            >
                <LoadingActivityDetails id={detailsId} role={'region'} spacing={1.5}>
                    {history.map((status, index) => (
                        <LoadingActivityText
                            key={`${status}-${index}`}
                            aria-live={index === history.length - 1 ? 'polite' : undefined}
                            variant={'p3'}
                        >
                            {status}
                        </LoadingActivityText>
                    ))}
                </LoadingActivityDetails>
            </Collapse>
        </LoadingActivityRow>
    );
};

const LoadingActivityRow = styled(Stack)(({ theme }) => ({
    alignItems: 'flex-start',
    marginTop: theme.spacing(-4),
}));

const LoadingActivitySummary = styled(Stack)(({ theme }) => ({
    alignItems: 'center',
    color: theme.palette.ensightful.neutral[600],
    minHeight: theme.spacing(5),
}));

const LoadingActivityHandle = styled('button')(({ theme }) => ({
    alignItems: 'center',
    background: 'transparent',
    border: 0,
    color: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    font: 'inherit',
    gap: theme.spacing(1.5),
    minHeight: theme.spacing(5),
    padding: 0,
    textAlign: 'left',

    '&:focus-visible': {
        outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
        outlineOffset: '1px',
    },
}));

const LoadingActivityToggleEntrance = styled('span')({
    display: 'inline-flex',

    '@keyframes hugoLoadingChevronEnter': {
        from: {
            opacity: 0,
            transform: 'translate(8px, -6px)',
        },
        to: {
            opacity: 1,
            transform: 'translate(0, 0)',
        },
    },

    animation: 'hugoLoadingChevronEnter 360ms ease-out',

    '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
    },
});

const LoadingActivityToggle = styled(Icon, {
    shouldForwardProp: (prop) => prop !== 'isExpanded',
})(({ theme, isExpanded }) => ({
    color: theme.palette.ensightful.neutral[600],
    transform: `rotate(${isExpanded ? 180 : 0}deg)`,
    transition: 'transform 220ms ease-out',
}));

const LoadingActivityText = styled(Text)(({ theme }) => ({
    color: theme.palette.ensightful.neutral[600],
    position: 'relative',

    '&::before': {
        backgroundColor: theme.palette.ensightful.neutral[400],
        content: '""',
        height: '100%',
        left: `-${theme.spacing(3)}`,
        position: 'absolute',
        top: 0,
        width: '1px',
    },

    '&:not(:last-child)::before': {
        height: `calc(100% + ${theme.spacing(1.5)})`,
    },

    '@keyframes hugoLoadingActivityEnter': {
        from: {
            opacity: 0,
            transform: 'translateY(-6px)',
        },
        to: {
            opacity: 1,
            transform: 'translateY(0)',
        },
    },

    animation: 'hugoLoadingActivityEnter 360ms ease-out',

    '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
    },
}));

const LoadingActivityTitle = styled(Text, {
    shouldForwardProp: (prop) => prop !== '$isEntering',
})(({ theme, $isEntering }) => ({
    color: theme.palette.ensightful.neutral[600],

    ...($isEntering && {
        '@keyframes hugoLoadingActivityTitleEnter': {
            from: {
                transform: `translateX(calc(-1rem - ${theme.spacing(1.5)}))`,
            },
            to: {
                transform: 'translateX(0)',
            },
        },

        animation: 'hugoLoadingActivityTitleEnter 360ms ease-out',

        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
        },
    }),
}));

const LoadingActivityDetails = styled(Stack)(({ theme }) => ({
    marginLeft: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    transformOrigin: 'top left',
}));

export default ChatLoadingActivity;
